import React, { createContext, useState, useContext, ReactNode } from "react";
import Role from "../apiService/role/roleApiService";

// ----------- Type Definitions -----------
interface RoleContextType {
  loading: boolean;
  error: string;
  roles: any[]; // List of dealer groups
  selectedRole: any | null; // Single dealer group details

  addRole: (role: any) => Promise<void>;
  deleteRole: (roleId: string) => Promise<void>;
  editRole: (roleId: string, data: any) => Promise<void>;
  getRole: (roleId: string) => Promise<void>;
  getAllRoles: () => Promise<void>;
}

// ----------- Context Create -----------
export const RoleContext = createContext<RoleContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const RoleProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any | null>(null);

  // ----------- API Calls Using Role Service -----------
  const addRole = async (role: any) => {
    setLoading(true);
    setError("");
    try {
      await Role.addRole(role);
      await getAllRoles(); // Refresh list after adding
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add role");
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async (roleId: string) => {
    setLoading(true);
    setError("");
    try {
      await Role.deleteRole(roleId);
      await getAllRoles(); // Refresh list after deletion
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete role");
    } finally {
      setLoading(false);
    }
  };

  const editRole = async (roleId: string, data: any) => {
    setLoading(true);
    setError("");
    try {
      await Role.editRole(roleId, data);
      await getAllRoles(); // Refresh list after update
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to edit role");
    } finally {
      setLoading(false);
    }
  };

  const getRole = async (roleId: string) => {
    setLoading(true);
    setError("");
    try {
      const response = await Role.getRole(roleId);
      setSelectedRole(response); // Store the single dealer group
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch role details");
    } finally {
      setLoading(false);
    }
  };

  const getAllRoles = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await Role.getAllRoles();
      setRoles(response.body); // Store the list of dealer groups
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  return (
    <RoleContext.Provider
      value={{
        loading,
        error,
        roles,
        selectedRole,
        addRole,
        deleteRole,
        editRole,
        getRole,
        getAllRoles
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
};
